import type { GridRowSpacingParams } from '@mui/x-data-grid';
import type { Theme } from '@mui/material/styles';
import palette from './palette';

const datagrid = {
  MuiDataGrid: {
    defaultProps: {
      columnHeaderHeight: 56,
      rowHeight: 48,
      rowSpacingType: 'margin',
      getRowSpacing: ({ isFirstVisible }: GridRowSpacingParams) => ({
        top: isFirstVisible ? 0 : 8,
        bottom: 0,
      }),
    },
    styleOverrides: {
      root: {
        '--DataGrid-rowBorderColor': palette.grey[200],
        '--unstable_DataGrid-radius': '6px',
        backgroundColor: 'transparent',
        border: 0,
        fontSize: '16px',
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
      },
      columnHeaders: {
        border: `1px solid ${palette.grey[200]}`,
        borderBottom: 0,
        '& .MuiDataGrid-row--borderBottom': {
          paddingBottom: 8,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: -1,
            right: -1,
            bottom: 0,
            height: 8,
            backgroundColor: palette.grey[150],
          },
        },
      },
      columnHeader: ({ theme }: { theme: Theme }) => ({
        '--DataGrid-t-header-background-base': 'transparent',
        transition: theme.transitions.create(
          ['background-color']
        ),
        '&:hover': {
          backgroundColor: palette.grey[100],
        },
      }),
      scrollbarFiller: {
        '--DataGrid-t-header-background-base': 'transparent',
      },
      row: ({ theme }: { theme: Theme }) => ({
        '--DataGrid-row-bg-rgb': '255 255 255',
        backgroundColor: 'rgb(var(--DataGrid-row-bg-rgb) / 1)',
        border: `1px solid ${palette.grey[200]}`,
        transition: theme.transitions.create(
          ['background-color', 'border-color', 'color']
        ),
        '&.MuiDataGrid-row--lastVisible': {
          borderBottomLeftRadius: 'var(--unstable_DataGrid-radius)',
          borderBottomRightRadius: 'var(--unstable_DataGrid-radius)',
        },
        '& .MuiSvgIcon-colorAction': {
          transition: theme.transitions.create(['color']),
        },
        '&:not(.MuiDataGrid-row--details):hover': {
          '--DataGrid-row-bg-rgb': '0 0 0',
          backgroundColor: 'rgb(var(--DataGrid-row-bg-rgb) / 1)',
          color: palette.common.white,
          '& .MuiIconButton-root, & .MuiSvgIcon-colorAction': {
            color: palette.common.white,
          },
          '& .MuiIconButton-colorSecondary:not(:hover, :hover:active)': {
            backgroundColor: palette.common.white,
          },
        },
        '&.MuiDataGrid-row--details:hover': {
          '--DataGrid-row-bg-rgb': '255 255 255',
          backgroundColor: 'rgb(var(--DataGrid-row-bg-rgb) / 1)',
        },
      }),
      cell: ({ theme }: { theme: Theme }) => ({
        border: 0,
        height: 'calc(var(--height) - 2px)',
        '&:focus, &:focus-within': {
          outline: 'none',
        },
        '&.MuiDataGrid-cell--gradientOverlay': {
          position: 'relative',
          textOverflow: 'clip',
          '&::after': {
            content: '""',
            backgroundColor: 'rgb(var(--DataGrid-row-bg-rgb) / 1)',
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 93,
            zIndex: 0,
            transition: theme.transitions.create(['background-color']),
            maskImage: 'linear-gradient(90deg, rgba(0, 0, 0, 0) -20.45%, rgba(0, 0, 0, 1) 100%)',
            maskRepeat: 'no-repeat',
            maskSize: '100% 100%',
          },
        },
      }),
    },
  },
};

export default datagrid;
